import { createContext, useContext } from 'react'
import {
  findResourceOfType,
  OpalApiRequestParams,
  OpalApiResponseDocument,
  useReadMoment,
} from '@opallabs/api'
import { toast } from '@opallabs/lapidary'
import {
  AddToPlanSuccessToast,
  addToPlanDefaultToastOptions,
} from '../AddToPlanToasts'
import {
  AddToPlanFailureToast,
  defaultAddToPlanFailureOptions,
} from '../AddToPlanToasts/AddToPlanFailureToast'
import { useOnAddToPlan } from '../queries/useOnAddToPlan'
import { MomentData, OnAddToPlanArgs, SelectionMeta } from '../types'

type AddToPlanModalContextValue = {
  moments: MomentData[]
  onAddToPlan: (args: OnAddToPlanArgs) => void
}

type QueryableAddTopPlanModalProviderProps = {
  momentUuid: string
  workspaceSlug: string
}

type MomentQueryParams = OpalApiRequestParams<'ReadMomentV3'>['query']

const query: MomentQueryParams = {
  fields: {
    moment: [
      'board_objects',
      'schedules',
      'title',
      'paired_block',
      'primary_story',
    ].join(','),
    board_object: 'board',
  },
  include: ['board_objects', 'schedules', 'primary_story'],
  expose: { moment: { meta: 'is_stamp_template' } },
}

const select = (
  responseDocument: OpalApiResponseDocument<'ReadMomentV3', 200>
): MomentData => {
  const momentSchedules =
    responseDocument.data.relationships.schedules?.data.map(
      (scheduleRelationship) => {
        const schedule = responseDocument.included?.find(
          findResourceOfType('moment_schedule', 'V3', scheduleRelationship.id)
        )

        return {
          startDate: schedule!.attributes.start_at,
          endDate: schedule!.attributes.end_at ?? null,
        }
      }
    )

  return {
    id: responseDocument.data.id,
    isStamp: Boolean(responseDocument.data.meta?.is_stamp_template),
    pairedBlockId:
      responseDocument.data.relationships.paired_block?.data?.id ?? null,
    schedule: momentSchedules?.[0] ?? null,
    title: responseDocument.data.attributes.title,
  }
}

const onSuccess = ({ plan, moments }: SelectionMeta, workspaceSlug: string) => {
  toast(
    (props) => (
      <AddToPlanSuccessToast
        {...props}
        plan={plan}
        moments={moments}
        workspaceSlug={workspaceSlug}
      />
    ),
    addToPlanDefaultToastOptions
  )
}

const onFailure = (planTitle: string, errorMessage?: string) => {
  toast(
    (props) => (
      <AddToPlanFailureToast
        {...props}
        errorMessage={errorMessage}
        momentCount={1}
        planTitle={planTitle}
      />
    ),
    defaultAddToPlanFailureOptions
  )
}

const QueryableAddToPlanModalContext = createContext<
  AddToPlanModalContextValue | undefined
>(undefined)

export const QueryableAddTopPlanModalProvider: React.FC<
  React.PropsWithChildren<QueryableAddTopPlanModalProviderProps>
> = ({ children, momentUuid, workspaceSlug }) => {
  const { data: moment } = useReadMoment({
    momentId: momentUuid,
    select,
    query,
  })

  const onAddToPlan = useOnAddToPlan({
    onSuccess: (data, selectionMeta) => {
      if (!selectionMeta) return
      if (data.successes.length) {
        onSuccess(selectionMeta, workspaceSlug)
      }
      if (data.failures.length)
        onFailure(selectionMeta.plan.title, data.failures[0]?.error)
    },
  })

  return (
    <QueryableAddToPlanModalContext.Provider
      value={{
        onAddToPlan,
        moments: moment ? [moment] : [],
      }}
    >
      {children}
    </QueryableAddToPlanModalContext.Provider>
  )
}

export const useQueryableAddToPlanModal = () =>
  useContext(QueryableAddToPlanModalContext)
